import React, { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { ClassicEditor, type EditorConfig } from "ckeditor5";
import { defaultEditorConfig } from "./defaultEditorConfig.ts";
import * as styles from "./CKEditor5.styles.ts";
import "ckeditor5/ckeditor5.css";
import "./CKEditor5.css";
import type { EventInfo } from "ckeditor5";
import germanTranslations from "ckeditor5/translations/de.js";
import englishTranslations from "ckeditor5/translations/en.js";
import type { CKEditorProps } from "./CKEditor5.types.ts";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { Placeholders } from "src/components/InputPlaceholders/InputPlaceholders.tsx";
import { Flex } from "src/components/Flex.tsx";
import { useCKEditorInsertion } from "src/components/CKEditor5/useCKeditorInsertion.ts";
import { INSERT_PLACEHOLDER_CHIP } from "src/components/CKEditor5/PlaceholderChip/placeholderChipModel.ts";
import { PlaceholderChip as PLACEHOLDER_CHIP_PLUGIN } from "src/components/CKEditor5/PlaceholderChip/PlaceholderChipPlugin.ts";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";
import { InputErrorMessage } from "src/components/InputErrorMessage/InputErrorMessage.tsx";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { getCookie } from "@exonn/utils";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

const placements = [
  "topLeft",
  "topRight",
  "bottomLeft",
  "bottomRight",
] as const;

await loadTranslations(TranslationNamespaceEnum.CkeditorComponent);

export function CKEditor5(props: CKEditorProps) {
  const {
    className,
    config: newConfig,
    replaceConfig = false,
    translatable,
    height,
    label,
    helperText,
    required,
    minHeight,
    placeholders,
    onSelectPlaceholder,
    maxHeight,
    onSetEditorRef,
    defaultValue,
    value,
    error,
    floatingError = false,
    additionalElems,
    disabled = false,
    isLoading = false,
    onFocus,
    onChange: onControlledChange,
    additionalLabelElems,
    placeholdersSize,
    ...otherProps
  } = props;

  const clientConfig = useClientConfig();
  const { i18n, t } = useTranslation(
    TranslationNamespaceEnum.CkeditorComponent,
  );
  const ckEditorLanguage = (i18n.resolvedLanguage ?? i18n.language).split(
    "-",
  )[0];

  const containerRef = useRef<HTMLDivElement | null>(null);
  // const editorRef = useRef<CKEditor<ClassicEditor> | null>(null);
  const [editorRef, setEditorRef] = useState<ClassicEditor | null>();
  const [editableRef, setEditableRef] = useState<HTMLElement | null>();

  const initialConfig: EditorConfig | undefined = replaceConfig
    ? newConfig
    : {
        language: ckEditorLanguage,
        ...defaultEditorConfig,
        style: {
          definitions: [
            {
              name: t("styles.articleCategory"),
              element: "h3",
              classes: ["category"],
            },
            {
              name: t("styles.title"),
              element: "h2",
              classes: ["document-title"],
            },
            {
              name: t("styles.subtitle"),
              element: "h3",
              classes: ["document-subtitle"],
            },
            {
              name: t("styles.infoBox"),
              element: "p",
              classes: ["info-box"],
            },
            {
              name: t("styles.sideQuote"),
              element: "blockquote",
              classes: ["side-quote"],
            },
            {
              name: t("styles.marker"),
              element: "span",
              classes: ["marker"],
            },
            {
              name: t("styles.spoiler"),
              element: "span",
              classes: ["spoiler"],
            },
            {
              name: t("styles.codeDark"),
              element: "pre",
              classes: ["fancy-code", "fancy-code-dark"],
            },
            {
              name: t("styles.codeBright"),
              element: "pre",
              classes: ["fancy-code", "fancy-code-bright"],
            },
          ],
        },
        simpleUpload: {
          uploadUrl: clientConfig.apiBaseUrl + "/system/ckeditor/upload",
          headers: {
            "x-xsrf-token": decodeURIComponent(getCookie("XSRF-TOKEN") || ""),
          },
          withCredentials: true,
        },
        ...(newConfig || {}),
        translations: [germanTranslations, englishTranslations],
      };

  const onChange = (_: EventInfo, editor: ClassicEditor) => {
    const data = editor.getData();

    if (disabled) {
      return;
    }

    if (editorRef && value !== editorRef.getData()) {
      onControlledChange?.(data);
    }
  };

  const onReady = (editor: ClassicEditor) => {
    setEditorRef(editor);
    onSetEditorRef?.(editor);
    setEditableRef(editor.ui.view.editable.element?.parentElement);
  };

  useEffect(() => {
    if (editorRef && value !== editorRef.getData()) {
      editorRef.setData(value ?? "");
    }
  }, [value, editorRef]);

  // Подписи чипов приходят с сервера позже, чем создаётся редактор, поэтому их
  // доносят отдельно: иначе на чипах остаются внутренние имена вида
  // `contact.VORNAME`.
  useEffect(() => {
    if (!editorRef?.plugins.has(PLACEHOLDER_CHIP_PLUGIN)) {
      return;
    }

    editorRef.plugins.get(PLACEHOLDER_CHIP_PLUGIN).setItems(placeholders ?? []);
  }, [editorRef, placeholders]);

  const { saveSelection, restoreSelection, insertText } =
    useCKEditorInsertion();

  const handlePlaceholderSelect = (placeholder: TextTemplatePlaceholder) => {
    onSelectPlaceholder?.(placeholder);
    const editor = editorRef;
    if (!editor) {
      return;
    }

    // Там, где подключён плагин чипов, плейсхолдер вставляется сразу предметом.
    // Вставленный текстом, он становился чипом лишь после сохранения и
    // повторного открытия формы — до тех пор человек правил голые скобки.
    const insertChip = editor.commands.get(INSERT_PLACEHOLDER_CHIP);

    if (insertChip !== undefined && placeholder.type !== "conditional") {
      editor.execute(INSERT_PLACEHOLDER_CHIP, { token: placeholder.value });
      onControlledChange?.(editor.getData());
      saveSelection(editor);
      restoreSelection(editor);

      return;
    }

    const code =
      placeholder.type === "conditional"
        ? `{% if ${placeholder.value} %} ... {% endif %}`
        : `{{ ${placeholder.value} }}`;
    const updated = insertText(editor, code);
    // Notify RHF
    onControlledChange?.(updated);

    // Restore caret inside CKEditor
    restoreSelection(editor);
  };

  // todo: wrap in error boundary

  return (
    <Flex flexDirection="column" gap={"15px"}>
      <Skeleton isLoading={isLoading}>
        <div>
          {label && (
            <InputLabel
              label={label}
              translatable={translatable}
              required={required}
              helperText={helperText}
              additionalElems={additionalLabelElems}
            />
          )}
          <Flex flexDirection="column" gap={"10px"}>
            {!!placeholders?.length && (
              <div
                style={{
                  marginTop: "5px",
                }}
              >
                <Placeholders
                  onSelectPlaceholder={handlePlaceholderSelect}
                  placeholders={placeholders}
                  disabled={disabled}
                  chipSize={placeholdersSize}
                />
              </div>
            )}

            <div
              ref={containerRef}
              className={className}
              css={styles.editor({
                height,
                error,
                minHeight,
                maxHeight,
                disabled,
              })}
            >
              <CKEditor
                key={ckEditorLanguage}
                data={defaultValue}
                editor={ClassicEditor}
                config={initialConfig}
                disabled={disabled}
                onReady={onReady}
                onChange={onChange}
                onFocus={(event, editor) => {
                  saveSelection(editor);
                  onFocus?.(event, editor);
                }}
                onBlur={(event, editor) => {
                  saveSelection(editor);
                }}
                {...otherProps}
              />

              {editableRef &&
                additionalElems &&
                placements.map((placement, index) => {
                  const element = additionalElems[placement];
                  return (
                    <Fragment key={index}>
                      {element &&
                        createPortal(
                          <div css={styles.additionalElem(placement)}>
                            {element}
                          </div>,
                          editableRef,
                        )}
                    </Fragment>
                  );
                })}
            </div>
          </Flex>

          {error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    </Flex>
  );
}
